<template>
  <div class="detail-root">

    <!-- Loading -->
    <div v-if="loading" class="state-center">
      <div class="spinner"></div>
      <p class="state-label">Loading agent…</p>
    </div>

    <!-- Not found -->
    <div v-else-if="!agent" class="state-center">
      <div class="state-icon"><i class="fas fa-user-slash"></i></div>
      <h2 class="state-title">Agent not found</h2>
      <p class="state-sub">This Top‑Up agent could not be loaded.</p>
      <button class="btn btn--back" @click="goBack">
        <i class="fas fa-arrow-left"></i> Back
      </button>
    </div>

    <!-- Content -->
    <template v-else>

      <!-- Header -->
      <div class="detail-header">
        <div class="detail-header__inner">
          <div class="detail-header__identity">
            <div class="avatar avatar--topup">
              <i class="fas fa-user-cog"></i>
            </div>
            <div>
              <div class="detail-header__top">
                <h1 class="detail-header__name">{{ agent.full_name }}</h1>
                <span class="type-badge type-badge--topup">
                  <i class="fas fa-user-cog"></i> Top‑Up Agent
                </span>
                <span v-if="agent.is_manager" class="badge-manager">
                  <i class="fas fa-crown"></i> Manager
                </span>
                <span v-if="!agent.is_account_active" class="badge-pending">
                  <i class="fas fa-ban"></i> Account Off
                </span>
                <span v-if="agent.is_deposit_blocked" class="badge-pending">
                  <i class="fas fa-lock"></i> Deposits blocked
                </span>
              </div>
              <p class="detail-header__meta">
                <i class="fas fa-calendar-alt"></i>
                Joined {{ formatDate(agent.date_joined) }}
                <span class="meta-sep">·</span>

                <span class="meta-sep">·</span>
                <i class="fas fa-id-card"></i> Serial {{ agent.serial_no || '—' }}
              </p>
            </div>
          </div>
          <button class="btn btn--back" @click="goBack">
            <i class="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="detail-body">

        <!-- Contact card -->
        <div class="info-card">
          <div class="info-card__title">
            <i class="fas fa-address-card"></i> Contact
          </div>
          <div class="info-row">
            <span class="info-row__icon"><i class="fas fa-envelope"></i></span>
            <div>
              <span class="info-row__label">Email</span>
              <span class="info-row__value">{{ agent.email || '—' }}</span>
            </div>
          </div>
          <div class="info-row">
            <span class="info-row__icon"><i class="fas fa-phone"></i></span>
            <div>
              <span class="info-row__label">Phone</span>
              <span class="info-row__value">{{ agent.phone_number || '—' }}</span>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div class="stat-single">
          <div class="stat-single__val">{{ clientsList.length }}</div>
          <div class="stat-single__lbl">
            <i class="fas fa-users"></i> Total clients
          </div>
        </div>

        <!-- Client list -->
        <div class="list-card">
          <div class="list-card__head">
            <span class="list-card__icon list-card__icon--green">
              <i class="fas fa-user-friends"></i>
            </span>
            <span class="list-card__title">Clients</span>
            <span class="list-card__count">{{ filteredClients.length }}</span>
          </div>

          <!-- Search -->
          <div class="search-bar">
            <i class="fas fa-search search-bar__icon"></i>
            <input
              v-model="search"
              type="text"
              class="search-bar__input"
              placeholder="Search by name, username or phone…"
            />
            <button v-if="search" class="search-bar__clear" @click="clearSearch" title="Clear">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Loading clients -->
          <div v-if="clientsLoading" class="clients-loading">
            <div class="spinner spinner--sm"></div>
          </div>

          <!-- Table -->
          <template v-else-if="filteredClients.length">
            <div class="table-wrap">
              <table class="ref-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th class="hide-sm">Email</th>
                    <th class="hide-sm">Phone</th>
                    <th>Status</th>
                    <th class="hide-sm">Saved</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in filteredClients" :key="c.user_id || c.username">
                    <td>
                      <div class="client-cell">
                        <span class="client-avatar">{{ initials(c.full_name) }}</span>
                        <div>
                          <span class="username-cell">{{ c.full_name }}</span>
                          <span class="client-username">@{{ c.username }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="muted hide-sm">{{ c.email || '—' }}</td>
                    <td class="muted hide-sm">{{ c.phone_number || '—' }}</td>
                    <td>
                      <span class="active-badge" :class="c.is_active ? 'active-badge--on' : 'active-badge--off'">
                        {{ c.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="muted hide-sm">{{ numFmt(c.saved_amount) }} F</td>
                    <td class="muted">{{ formatDate(c.date_registered || c.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="pagination-info">
              {{ filteredClients.length }} clients shown
              <span v-if="search"> matching "{{ search }}"</span>
            </div>
          </template>

          <!-- Empty -->
          <div v-else class="empty-state">
            <i :class="search ? 'fas fa-search' : 'fas fa-user-slash'"></i>
            <p v-if="search">No clients match <strong>"{{ search }}"</strong>.</p>
            <p v-else>No clients registered yet under this agent.</p>
            <button v-if="search" class="btn-link" @click="clearSearch">Clear search</button>
          </div>
        </div>

      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TopupDetail',
  data() {
    return {
      agent: null,
      loading: false,
      clientsList: [],
      clientsLoading: false,
      search: '',
    }
  },
  computed: {
    filteredClients() {
      if (!this.search) return this.clientsList
      const q = this.search.toLowerCase()
      return this.clientsList.filter(c =>
        (c.full_name && c.full_name.toLowerCase().includes(q)) ||
        (c.username && c.username.toLowerCase().includes(q)) ||
        (c.phone_number && c.phone_number.includes(q))
      )
    }
  },
  mounted() {
    this.fetchAgentAndClients()
  },
  methods: {
    async fetchAgentAndClients() {
      const idFromUrl = parseInt(this.$route.params.id)
      if (isNaN(idFromUrl)) {
        this.loading = false
        return
      }
      this.loading = true
      try {
        // 1. Charge la liste et cherche par id OU user_id
        const res = await axios.get('/api/v1/manager/topup/accounts/')
        const topups = res.data.topups || []
        this.agent = topups.find(t => t.id === idFromUrl || t.user_id === idFromUrl) || null

        if (!this.agent) {
          this.loading = false
          return
        }

        // 2. Charge les clients avec le UserProfile.id (obligatoire)
        await this.fetchClients(this.agent.id)
      } catch (err) {
        console.error(err)
        this.showSnack('Error loading agent data')
      } finally {
        this.loading = false
      }
    },

    async fetchClients(profileId) {
      this.clientsLoading = true
      try {
        const res = await axios.get(`/api/v1/manager/topup/accounts/${profileId}/clients/`)
        this.clientsList = res.data.clients || []
      } catch (err) {
        console.error(err)
        this.showSnack('Error loading clients')
      } finally {
        this.clientsLoading = false
      }
    },

    clearSearch() {
      this.search = ''
    },

    // ── Helpers ────────────────────────────────────
    formatDate(date) {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    },

    initials(name) {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    },

    numFmt(value) {
      if (!value) return '0'
      return Number(value).toLocaleString('en-US')
    },

    goBack() {
      window.history.back()
    },

    showSnack(message, color = 'error') {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color === 'error' ? 'red darken-4' : 'green darken-3')
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════════
   Exactement les mêmes styles que PartnerDetail (adaptés topup)
   ═══════════════════════════════════════════════════════════════════════ */
.detail-root {
  padding: 20px;
  background: #f4f6fb;
  min-height: 100vh;
  font-family: 'Inter', system-ui, sans-serif;
  color: #2d3142;
}

/* ── States ─────────────────────────────────── */
.state-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #eef0fc;
  border-top-color: #5c6bc0;
  border-radius: 50%;
  animation: spin .8s linear infinite;
  margin-bottom: 16px;
}
.spinner--sm { width: 28px; height: 28px; border-width: 3px; margin: 0; }
@keyframes spin { to { transform: rotate(360deg); } }

.state-icon {
  width: 72px;
  height: 72px;
  background: #f4f6fb;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #b0b6cc;
  margin-bottom: 16px;
}
.state-label, .state-sub { font-size: 14px; color: #9aa0ba; margin: 0; }
.state-title { font-size: 20px; font-weight: 700; color: #2d3142; margin: 8px 0 4px; }

/* ── Header ─────────────────────────────────── */
.detail-header {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(44, 51, 73, .07);
  margin-bottom: 20px;
}
.detail-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  flex-wrap: wrap;
  gap: 14px;
}
.detail-header__identity {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.avatar {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.avatar--topup { background: #e3f2fd; color: #1565c0; }

.detail-header__top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.detail-header__name { font-size: 22px; font-weight: 800; color: #1a1a2e; margin: 0; }
.detail-header__meta {
  font-size: 13px;
  color: #9aa0ba;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.meta-sep { color: #d0d4e2; }

/* ── Badges ──────────────────────────────────── */
.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid transparent;
}
.type-badge--topup { background: #e3f2fd; color: #1565c0; border-color: #bbdefb; }
.badge-manager {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: #f3e5f5;
  color: #6a1b9a;
  border: 1px solid #e1bee7;
}
.badge-pending {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: #fff8e1;
  color: #f57f17;
  border: 1px solid #ffe082;
}

/* ── Back button ─────────────────────────────── */
.btn--back {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: 1.5px solid #e9ecf5;
  background: #f9fafc;
  color: #5c6bc0;
  cursor: pointer;
  transition: all .15s ease;
}
.btn--back:hover { background: #eef0fc; border-color: #5c6bc0; }

/* ── Body ────────────────────────────────────── */
.detail-body { display: flex; flex-direction: column; gap: 16px; }

/* ── Contact card ────────────────────────────── */
.info-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #f0f2f8;
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.info-card__title {
  font-size: 12px;
  font-weight: 700;
  color: #5c6bc0;
  text-transform: uppercase;
  letter-spacing: .5px;
  display: flex;
  align-items: center;
  gap: 7px;
}
.info-row { display: flex; align-items: flex-start; gap: 12px; }
.info-row__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #eef0fc;
  color: #5c6bc0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.info-row__label { display: block; font-size: 11px; color: #9aa0ba; font-weight: 600; margin-bottom: 2px; }
.info-row__value { display: block; font-size: 14px; font-weight: 600; color: #2d3142; }

/* ── Stat single ─────────────────────────────── */
.stat-single {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #f0f2f8;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}
.stat-single__val { font-size: 36px; font-weight: 800; color: #2e7d32; line-height: 1; }
.stat-single__lbl {
  font-size: 14px;
  font-weight: 600;
  color: #6b7a99;
  display: flex;
  align-items: center;
  gap: 7px;
}
.stat-single__lbl i { color: #2e7d32; }

/* ── List card ───────────────────────────────── */
.list-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #f0f2f8;
  overflow: hidden;
}
.list-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f2f8;
}
.list-card__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}
.list-card__icon--green { background: #e8f5e9; color: #2e7d32; }
.list-card__title { font-size: 14px; font-weight: 700; color: #2d3142; flex: 1; }
.list-card__count {
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  background: #f4f6fb;
  color: #5c6bc0;
}

/* ── Search bar ──────────────────────────────── */
.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f8;
  background: #fafbfd;
}
.search-bar__icon { color: #9aa0ba; font-size: 13px; flex-shrink: 0; }
.search-bar__input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: #2d3142;
  background: transparent;
  font-family: inherit;
}
.search-bar__input::placeholder { color: #b0b6cc; }
.search-bar__clear {
  background: none;
  border: none;
  color: #b0b6cc;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: color .15s;
}
.search-bar__clear:hover { color: #5c6bc0; }

/* ── Clients loading ─────────────────────────── */
.clients-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
}

/* ── Table ───────────────────────────────────── */
.table-wrap { overflow-x: auto; }
.ref-table { width: 100%; border-collapse: collapse; }
.ref-table th {
  font-size: 11px;
  font-weight: 700;
  color: #9aa0ba;
  text-transform: uppercase;
  letter-spacing: .5px;
  padding: 10px 16px;
  text-align: left;
  background: #f9fafc;
  border-bottom: 1px solid #f0f2f8;
  white-space: nowrap;
}
.ref-table td {
  padding: 11px 16px;
  font-size: 13px;
  border-bottom: 1px solid #f9fafc;
  vertical-align: middle;
}
.ref-table tr:last-child td { border-bottom: none; }
.ref-table tr:hover td { background: #fafbfd; }
.client-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
}
.client-avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #eef0fc;
  color: #5c6bc0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
}
.username-cell { display: block; font-weight: 700; color: #2d3142; }
.client-username { display: block; font-size: 11px; color: #9aa0ba; margin-top: 1px; }
.muted { color: #6b7a99; }

/* ── Active badge ────────────────────────────── */
.active-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.active-badge--on  { background: #e8f5e9; color: #2e7d32; }
.active-badge--off { background: #ffebee; color: #c62828; }

/* ── Pagination ──────────────────────────────── */
.pagination-info {
  text-align: center;
  font-size: 12px;
  color: #9aa0ba;
  padding: 0 16px 12px;
}
.pagination-info strong { color: #5c6bc0; }

/* ── Empty state ─────────────────────────────── */
.empty-state {
  padding: 56px 24px;
  text-align: center;
  color: #9aa0ba;
}
.empty-state i { font-size: 32px; margin-bottom: 12px; display: block; opacity: .45; }
.empty-state p { margin: 0 0 14px; font-size: 14px; }
.empty-state strong { color: #5c6bc0; }

.btn-link {
  background: none;
  border: none;
  color: #5c6bc0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  text-decoration: underline;
  padding: 0;
}
.btn-link:hover { color: #3949ab; }

/* ── Responsive ──────────────────────────────── */
@media (max-width: 600px) {
  .detail-root { padding: 12px; }
  .detail-header__name { font-size: 18px; }
  .hide-sm { display: none; }
  .stat-single__val { font-size: 28px; }
}
</style>
